import { useRef, useEffect, useCallback } from 'react';
import { X } from 'lucide-react';
import type { QuerySchema } from './schema';
import { useI18n } from '@/context/I18nContext';

interface GenericSearchHelpDialogProps {
  schema: QuerySchema<string>;
  open: boolean;
  onClose: () => void;
}

export function GenericSearchHelpDialog({
  schema,
  open,
  onClose,
}: GenericSearchHelpDialogProps) {
  const { t } = useI18n();
  const ref = useRef<HTMLDialogElement>(null);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) {
      el.showModal();
    } else if (!open && el.open) {
      el.close();
    }
  }, [open]);

  if (!open) return null;

  const fieldsList = Object.values(schema.fields);

  return (
    <dialog
      ref={ref}
      onCancel={(e) => {
        e.preventDefault();
        handleClose();
      }}
      onClick={(e) => {
        if (e.target === ref.current) handleClose();
      }}
      className="
        w-full max-w-[560px] max-h-[80vh]
        bg-bg-raised border border-border-subtle rounded-xl
        text-text-primary
        p-0 m-auto
        max-sm:max-w-full max-sm:max-h-full max-sm:h-full max-sm:rounded-none
      "
      style={{ colorScheme: 'dark' }}
    >
      <div className="flex flex-col h-full max-h-[80vh] max-sm:max-h-full">
        {/* Header */}
        <div className="flex items-start justify-between px-5 pt-5 pb-3 border-b border-border-subtle">
          <div>
            <h3 className="text-sm font-semibold tracking-tight">
              {schema.title ? t(schema.title as any) : t('search.title')}
            </h3>
            <p className="text-[0.7rem] text-text-muted mt-1">
              {schema.subtitle ? t(schema.subtitle as any) : t('search.subtitle')}
            </p>
          </div>
          <button
            onClick={handleClose}
            aria-label={t('search.closeDialog')}
            className="p-1.5 rounded text-text-muted hover:text-text-primary transition-colors cursor-pointer shrink-0"
          >
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {/* Bare search explanation if default field exists */}
          {schema.defaultField && (
            <div className="mb-4 pb-3 border-b border-border-subtle">
              <div className="flex items-baseline gap-2">
                <code className="text-[0.78rem] font-semibold text-accent-hover font-mono">
                  &lt;text&gt;
                </code>
              </div>
              <p className="text-[0.75rem] text-text-secondary leading-relaxed mt-0.5">
                {t('search.field.bareTextDesc')}
              </p>
            </div>
          )}

          {/* Fields Reference */}
          <div className="space-y-3">
            {fieldsList.map((field) => {
              const example = field.example || `${field.key}:value`;
              return (
                <div key={field.key} className="flex flex-col gap-1">
                  <div className="flex items-baseline gap-2">
                    <code className="text-[0.78rem] font-semibold text-accent-hover font-mono">
                      {field.key}:&lt;{field.type}&gt;
                    </code>
                    <span className="text-[0.72rem] text-text-muted font-medium">({t(field.label as any)})</span>
                  </div>
                  {field.description && (
                    <p className="text-[0.75rem] text-text-secondary leading-relaxed">
                      {t(field.description as any)}
                    </p>
                  )}
                  <code className="text-[0.7rem] text-text-muted font-mono bg-bg-surface px-2 py-1 rounded w-fit">
                    {example}
                  </code>
                </div>
              );
            })}
          </div>

          {/* Operators & Logic */}
          <div className="mt-5 pt-4 border-t border-border-subtle">
            <h4 className="text-[0.75rem] font-semibold text-text-secondary uppercase tracking-wider mb-3">
              {t('search.operatorsTitle')}
            </h4>
            <div className="space-y-2 text-[0.75rem] text-text-secondary leading-relaxed">
              <p>
                <strong className="text-text-primary">{t('search.numericComparisons')}</strong>{' '}
                <code className="text-accent-hover font-mono">=</code>,{' '}
                <code className="text-accent-hover font-mono">&gt;</code>,{' '}
                <code className="text-accent-hover font-mono">&gt;=</code>,{' '}
                <code className="text-accent-hover font-mono">&lt;</code>,{' '}
                <code className="text-accent-hover font-mono">&lt;=</code>
              </p>
              <p>
                <strong className="text-text-primary">{t('search.range')}</strong>{' '}
                <code className="text-accent-hover font-mono">5..20</code> {t('search.rangeDesc')}
              </p>
              <p>
                <strong className="text-text-primary">{t('search.booleanLogic')}</strong> {t('search.booleanLogicDesc')}{' '}
                <code className="text-accent-hover font-mono">AND</code>. {t('search.booleanLogicUse' as any)}{' '}
                <code className="text-accent-hover font-mono">OR</code> {t('search.booleanLogicOr' as any)}{' '}
                <code className="text-accent-hover font-mono">( )</code> {t('search.booleanLogicGroup' as any)}
              </p>
              <p>
                <strong className="text-text-primary">{t('search.quotes')}</strong> {t('search.quotesDesc')}{' '}
                <code className="text-accent-hover font-mono">name:"Spa Francorchamps"</code>.
              </p>
            </div>
          </div>

          {/* Examples */}
          {schema.examples && schema.examples.length > 0 && (
            <div className="mt-5 pt-4 border-t border-border-subtle">
              <h4 className="text-[0.75rem] font-semibold text-text-secondary uppercase tracking-wider mb-3">
                {t('search.examplesTitle')}
              </h4>
              <div className="space-y-2">
                {schema.examples.map(({ q, desc }) => (
                  <div key={q} className="flex flex-col gap-0.5">
                    <code className="text-[0.72rem] text-accent-hover font-mono bg-bg-surface px-2 py-1 rounded w-fit">
                      {q}
                    </code>
                    <span className="text-[0.7rem] text-text-muted">{t(desc as any)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </dialog>
  );
}
